import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { 
  Package, Building2, Users, Truck, Plus, Edit, Trash2, X,
  Loader2, Upload, Search, ChevronDown, Layers
} from 'lucide-react';

const StockModify = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [offices, setOffices] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [combos, setCombos] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Form states
  const [formData, setFormData] = useState({});

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const [productsRes, officesRes, vendorsRes, customersRes, combosRes] = await Promise.all([
        api.getStockProducts(false),
        api.getStockOffices(false),
        api.getStockVendors(false),
        api.getStockCustomers(false),
        api.getStockCombos(false)
      ]);
      setProducts(productsRes.data);
      setOffices(officesRes.data);
      setVendors(vendorsRes.data);
      setCustomers(customersRes.data);
      setCombos(combosRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (type, item = null) => {
    setModalType(type);
    setEditingItem(item);
    
    if (item) {
      setFormData({ ...item });
    } else {
      // Default form data based on type
      switch (type) {
        case 'product':
          setFormData({ sku: '', name: '', description: '', category: '', unit: 'pcs' });
          break;
        case 'office':
          setFormData({ name: '', address: '', type: 'warehouse' });
          break;
        case 'vendor':
          setFormData({ name: '', contact_person: '', phone: '', email: '', address: '' });
          break;
        case 'customer':
          setFormData({ name: '', contact_person: '', phone: '', email: '', address: '' });
          break;
        case 'combo':
          setFormData({ sku: '', name: '', description: '', items: [] });
          break;
        default:
          setFormData({});
      }
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setFormData({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingItem) {
        // Update
        switch (modalType) {
          case 'product':
            await api.updateStockProduct(editingItem.id, formData);
            break;
          case 'office':
            await api.updateStockOffice(editingItem.id, formData);
            break;
          case 'vendor':
            await api.updateStockVendor(editingItem.id, formData);
            break;
          case 'customer':
            await api.updateStockCustomer(editingItem.id, formData);
            break;
          case 'combo':
            await api.updateStockCombo(editingItem.id, {
              ...formData,
              items: formData.items.map(i => ({ product_id: i.product_id, quantity: i.quantity }))
            });
            break;
        }
        toast.success(`${modalType} updated successfully`);
      } else {
        // Create
        switch (modalType) {
          case 'product':
            await api.createStockProduct(formData);
            break;
          case 'office':
            await api.createStockOffice(formData);
            break;
          case 'vendor':
            await api.createStockVendor(formData);
            break;
          case 'customer':
            await api.createStockCustomer(formData);
            break;
          case 'combo':
            await api.createStockCombo({
              ...formData,
              items: formData.items.map(i => ({ product_id: i.product_id, quantity: i.quantity }))
            });
            break;
        }
        toast.success(`${modalType} created successfully`);
      }
      
      await fetchAllData();
      closeModal();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to save ${modalType}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm(`Are you sure you want to deactivate this ${type}?`)) return;

    try {
      switch (type) {
        case 'product':
          await api.deleteStockProduct(id);
          break;
        case 'office':
          await api.deleteStockOffice(id);
          break;
        case 'vendor':
          await api.deleteStockVendor(id);
          break;
        case 'customer':
          await api.deleteStockCustomer(id);
          break;
        case 'combo':
          await api.deleteStockCombo(id);
          break;
      }
      toast.success(`${type} deactivated`);
      await fetchAllData();
    } catch (error) {
      toast.error(`Failed to delete ${type}`);
    }
  };

  const handleBulkImport = async (type, file) => {
    try {
      if (type === 'product') {
        const result = await api.bulkImportProducts(file);
        toast.success(`Imported ${result.data.created} products, ${result.data.skipped} skipped`);
      } else if (type === 'customer') {
        const result = await api.bulkImportCustomers(file);
        toast.success(`Imported ${result.data.created} customers, ${result.data.skipped} skipped`);
      }
      await fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    }
  };

  const tabs = [
    { id: 'products', label: 'Products', icon: Package, count: products.length },
    { id: 'combos', label: 'Combos', icon: Layers, count: combos.length },
    { id: 'offices', label: 'Offices/Warehouses', icon: Building2, count: offices.length },
    { id: 'vendors', label: 'Vendors', icon: Truck, count: vendors.length },
    { id: 'customers', label: 'Customers', icon: Users, count: customers.length },
  ];

  const getActiveData = () => {
    let data = [];
    switch (activeTab) {
      case 'products':
        data = products;
        break;
      case 'offices':
        data = offices;
        break;
      case 'vendors':
        data = vendors;
        break;
      case 'customers':
        data = customers;
        break;
      case 'combos':
        data = combos;
        break;
      default:
        data = [];
    }
    
    if (searchTerm) {
      return data.filter(item => 
        item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.sku?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return data;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div data-testid="stock-modify" className="max-w-[1600px] mx-auto space-y-6">
      <div>
        <h1 className="text-4xl font-bold font-headings uppercase tracking-tight text-slate-900 mb-2">
          Modify Stock Data
        </h1>
        <p className="text-slate-600 font-body">Manage products, warehouses, vendors, and customers.</p>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="flex border-b border-slate-200 overflow-x-auto">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setSearchTerm(''); }}
                className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'text-cyan-600 border-b-2 border-cyan-600 bg-cyan-50/50'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                }`}
              >
                <Icon size={18} />
                {tab.label}
                <span className="ml-2 bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full text-xs">
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Toolbar */}
        <div className="p-4 bg-slate-50 border-b border-slate-200 flex flex-wrap items-center justify-between gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="text"
              placeholder={`Search ${activeTab}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
            />
          </div>
          
          <div className="flex items-center gap-3">
            {/* Bulk Import (Products & Customers only) */}
            {(activeTab === 'products' || activeTab === 'customers') && (
              <label className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors">
                <Upload size={18} />
                <span className="text-sm font-medium">Bulk Import</span>
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files[0]) {
                      handleBulkImport(activeTab === 'products' ? 'product' : 'customer', e.target.files[0]);
                      e.target.value = '';
                    }
                  }}
                />
              </label>
            )}
            
            <button
              onClick={() => openModal(activeTab.slice(0, -1))}
              className="flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors"
            >
              <Plus size={18} />
              Add {activeTab.slice(0, -1)}
            </button>
          </div>
        </div>

        {/* Data Table */}
        <div className="overflow-x-auto">
          {activeTab === 'products' && (
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-6 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">SKU</th>
                  <th className="text-left px-6 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Name</th>
                  <th className="text-left px-6 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Category</th>
                  <th className="text-left px-6 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Unit</th>
                  <th className="text-center px-6 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Status</th>
                  <th className="text-center px-6 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {getActiveData().length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-12 text-slate-500">
                      <Package size={48} className="mx-auto mb-4 text-slate-300" />
                      <p>No products found</p>
                    </td>
                  </tr>
                ) : (
                  getActiveData().map(item => (
                    <tr key={item.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4">
                        <span className="font-mono text-sm bg-slate-100 px-2 py-1 rounded">{item.sku}</span>
                      </td>
                      <td className="px-6 py-4 font-medium text-slate-900">{item.name}</td>
                      <td className="px-6 py-4 text-slate-600">{item.category || '-'}</td>
                      <td className="px-6 py-4 text-slate-600">{item.unit}</td>
                      <td className="px-6 py-4 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          item.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {item.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => openModal('product', item)}
                          className="p-1 text-slate-400 hover:text-cyan-600 mr-2"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete('product', item.id)}
                          className="p-1 text-slate-400 hover:text-red-600"
                        >
                          <Trash2 size={18} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}

          {activeTab === 'offices' && (
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-6 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Name</th>
                  <th className="text-left px-6 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Type</th>
                  <th className="text-left px-6 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Address</th>
                  <th className="text-center px-6 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Status</th>
                  <th className="text-center px-6 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {getActiveData().length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-12 text-slate-500">
                      <Building2 size={48} className="mx-auto mb-4 text-slate-300" />
                      <p>No offices found</p>
                    </td>
                  </tr>
                ) : (
                  getActiveData().map(item => (
                    <tr key={item.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4 font-medium text-slate-900">{item.name}</td>
                      <td className="px-6 py-4 text-slate-600 capitalize">{item.type}</td>
                      <td className="px-6 py-4 text-slate-600">{item.address || '-'}</td>
                      <td className="px-6 py-4 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          item.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {item.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => openModal('office', item)}
                          className="p-1 text-slate-400 hover:text-cyan-600 mr-2"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete('office', item.id)}
                          className="p-1 text-slate-400 hover:text-red-600"
                        >
                          <Trash2 size={18} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}

          {activeTab === 'vendors' && (
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-6 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Name</th>
                  <th className="text-left px-6 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Contact Person</th>
                  <th className="text-left px-6 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Phone</th>
                  <th className="text-left px-6 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Email</th>
                  <th className="text-center px-6 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Status</th>
                  <th className="text-center px-6 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {getActiveData().length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-12 text-slate-500">
                      <Truck size={48} className="mx-auto mb-4 text-slate-300" />
                      <p>No vendors found</p>
                    </td>
                  </tr>
                ) : (
                  getActiveData().map(item => (
                    <tr key={item.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4 font-medium text-slate-900">{item.name}</td>
                      <td className="px-6 py-4 text-slate-600">{item.contact_person || '-'}</td>
                      <td className="px-6 py-4 text-slate-600">{item.phone || '-'}</td>
                      <td className="px-6 py-4 text-slate-600">{item.email || '-'}</td>
                      <td className="px-6 py-4 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          item.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {item.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => openModal('vendor', item)}
                          className="p-1 text-slate-400 hover:text-cyan-600 mr-2"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete('vendor', item.id)}
                          className="p-1 text-slate-400 hover:text-red-600"
                        >
                          <Trash2 size={18} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}

          {activeTab === 'customers' && (
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-6 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Name</th>
                  <th className="text-left px-6 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Contact Person</th>
                  <th className="text-left px-6 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Phone</th>
                  <th className="text-left px-6 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Email</th>
                  <th className="text-center px-6 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Status</th>
                  <th className="text-center px-6 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {getActiveData().length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-12 text-slate-500">
                      <Users size={48} className="mx-auto mb-4 text-slate-300" />
                      <p>No customers found</p>
                    </td>
                  </tr>
                ) : (
                  getActiveData().map(item => (
                    <tr key={item.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4 font-medium text-slate-900">{item.name}</td>
                      <td className="px-6 py-4 text-slate-600">{item.contact_person || '-'}</td>
                      <td className="px-6 py-4 text-slate-600">{item.phone || '-'}</td>
                      <td className="px-6 py-4 text-slate-600">{item.email || '-'}</td>
                      <td className="px-6 py-4 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          item.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {item.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => openModal('customer', item)}
                          className="p-1 text-slate-400 hover:text-cyan-600 mr-2"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete('customer', item.id)}
                          className="p-1 text-slate-400 hover:text-red-600"
                        >
                          <Trash2 size={18} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}

          {activeTab === 'combos' && (
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-6 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">SKU</th>
                  <th className="text-left px-6 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Combo Name</th>
                  <th className="text-left px-6 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Items</th>
                  <th className="text-center px-6 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Status</th>
                  <th className="text-center px-6 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {getActiveData().length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-12 text-slate-500">
                      <Layers size={48} className="mx-auto mb-4 text-slate-300" />
                      <p>No combos found</p>
                      <p className="text-sm mt-2">Create a combo to bundle multiple books together</p>
                    </td>
                  </tr>
                ) : (
                  getActiveData().map(item => (
                    <tr key={item.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4">
                        <span className="font-mono text-sm bg-purple-100 text-purple-700 px-2 py-1 rounded">{item.sku}</span>
                      </td>
                      <td className="px-6 py-4">
                        <p className="font-medium text-slate-900">{item.name}</p>
                        {item.description && <p className="text-sm text-slate-500">{item.description}</p>}
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          {item.items?.slice(0, 3).map((i, idx) => (
                            <div key={idx} className="text-sm">
                              <span className="font-mono text-xs bg-slate-100 px-1 rounded">{i.product_sku}</span>
                              <span className="text-slate-600 ml-2">x{i.quantity}</span>
                            </div>
                          ))}
                          {item.items?.length > 3 && (
                            <span className="text-xs text-slate-500">+{item.items.length - 3} more</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          item.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {item.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => openModal('combo', item)}
                          className="p-1 text-slate-400 hover:text-cyan-600 mr-2"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete('combo', item.id)}
                          className="p-1 text-slate-400 hover:text-red-600"
                        >
                          <Trash2 size={18} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <h3 className="text-xl font-semibold text-slate-900 capitalize">
                {editingItem ? `Edit ${modalType}` : `Add ${modalType}`}
              </h3>
              <button onClick={closeModal} className="p-2 text-slate-400 hover:text-slate-600">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {modalType === 'product' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">SKU *</label>
                    <input
                      type="text"
                      value={formData.sku || ''}
                      onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                      placeholder="e.g., SKU-001"
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
                    <input
                      type="text"
                      value={formData.name || ''}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Product name"
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                    <textarea
                      value={formData.description || ''}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Optional description"
                      rows={2}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
                      <input
                        type="text"
                        value={formData.category || ''}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        placeholder="e.g., Books"
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Unit</label>
                      <select
                        value={formData.unit || 'pcs'}
                        onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                      >
                        <option value="pcs">Pieces</option>
                        <option value="box">Box</option>
                        <option value="kg">Kilogram</option>
                        <option value="set">Set</option>
                      </select>
                    </div>
                  </div>
                </>
              )}

              {modalType === 'office' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
                    <input
                      type="text"
                      value={formData.name || ''}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Office/Warehouse name"
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Type</label>
                    <select
                      value={formData.type || 'warehouse'}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                    >
                      <option value="warehouse">Warehouse</option>
                      <option value="office">Office</option>
                      <option value="store">Store</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Address</label>
                    <textarea
                      value={formData.address || ''}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      placeholder="Full address"
                      rows={2}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>
                </>
              )}

              {(modalType === 'vendor' || modalType === 'customer') && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
                    <input
                      type="text"
                      value={formData.name || ''}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder={`${modalType} name`}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Contact Person</label>
                    <input
                      type="text"
                      value={formData.contact_person || ''}
                      onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                      placeholder="Contact name"
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                      <input
                        type="text"
                        value={formData.phone || ''}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="Phone number"
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                      <input
                        type="email"
                        value={formData.email || ''}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="Email address"
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Address</label>
                    <textarea
                      value={formData.address || ''}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      placeholder="Full address"
                      rows={2}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>
                </>
              )}

              {modalType === 'combo' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Combo SKU *</label>
                      <input
                        type="text"
                        value={formData.sku || ''}
                        onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                        placeholder="e.g., COMBO-SSC-001"
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Combo Name *</label>
                      <input
                        type="text"
                        value={formData.name || ''}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="e.g., SSC Complete Set"
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                    <input
                      type="text"
                      value={formData.description || ''}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Optional description"
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>
                  
                  {/* Combo Items */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-slate-700">Books in Combo *</label>
                      <button
                        type="button"
                        onClick={() => setFormData({ 
                          ...formData, 
                          items: [...(formData.items || []), { product_id: '', quantity: 1 }] 
                        })}
                        className="text-sm text-cyan-600 hover:text-cyan-700 flex items-center gap-1"
                      >
                        <Plus size={16} /> Add Book
                      </button>
                    </div>
                    
                    {(!formData.items || formData.items.length === 0) && (
                      <p className="text-sm text-slate-500 py-4 text-center border border-dashed border-slate-300 rounded-lg">
                        Click "Add Book" to add products to this combo
                      </p>
                    )}
                    
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {formData.items?.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                          <select
                            value={item.product_id}
                            onChange={(e) => {
                              const newItems = [...formData.items];
                              const product = products.find(p => p.id === e.target.value);
                              newItems[idx] = { 
                                ...newItems[idx], 
                                product_id: e.target.value,
                                product_sku: product?.sku,
                                product_name: product?.name
                              };
                              setFormData({ ...formData, items: newItems });
                            }}
                            className="flex-1 px-3 py-1.5 border border-slate-300 rounded-lg text-sm"
                            required
                          >
                            <option value="">Select Book</option>
                            {products.filter(p => p.is_active).map(p => (
                              <option key={p.id} value={p.id}>{p.sku} - {p.name}</option>
                            ))}
                          </select>
                          <div className="flex items-center gap-1">
                            <span className="text-sm text-slate-500">×</span>
                            <input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => {
                                const newItems = [...formData.items];
                                newItems[idx] = { ...newItems[idx], quantity: parseInt(e.target.value) || 1 };
                                setFormData({ ...formData, items: newItems });
                              }}
                              className="w-16 px-2 py-1.5 border border-slate-300 rounded-lg text-sm text-center"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const newItems = formData.items.filter((_, i) => i !== idx);
                              setFormData({ ...formData, items: newItems });
                            }}
                            className="p-1 text-red-500 hover:bg-red-50 rounded"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {submitting && <Loader2 className="animate-spin" size={18} />}
                  {editingItem ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockModify;
